import { useAuth } from "@/contexts/auth-context";
import { usePendencias } from "@/contexts/pendencias-context";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { MdHelpOutline } from "react-icons/md";
import { Link, useLocation } from "react-router-dom";
import { Button } from "../ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import { menuItems } from "./nav-items";
import { UserSessionMenu } from "./user-session-menu";

const supportLinks = [
  {
    label: "Abrir chamado",
    to: "/cliente/ajuda/abrir-chamado",
    color: "bg-n-green-light",
  },
  {
    label: "Chamados abertos",
    to: "/cliente/ajuda/chamados-abertos",
    color: "bg-n-green-light",
  },
  {
    label: "Histórico de chamados",
    to: "/cliente/ajuda/historico-de-chamados",
    color: "bg-n-green-light",
  },
  {
    label: "Perguntas Frequentes",
    to: "/cliente/ajuda/perguntas-frequentes",
    color: "bg-n-green-light",
  },
];

export default function Sidebar() {
  const { pathname } = useLocation();
  const blockActive = supportLinks.some((l) => pathname.startsWith(l.to));
  const [faqOpen, setFaqOpen] = useState(blockActive);
  const { user, signOut } = useAuth();
  const { getSidebarAlerts } = usePendencias();

  const sidebarAlerts = getSidebarAlerts();

  useEffect(() => {
    setFaqOpen(blockActive);
  }, [pathname, blockActive]);

  const isActive = (path: string) => pathname === path;
  const displayName = user?.cli_fantasia || "Usuário";
  const email = user?.email || "email@dominio.com";

  return (
    <aside className="hidden lg:flex w-[300px] shrink-0 self-start sticky top-0 h-screen box-border pt-[72px] overflow-y-auto bg-n-bg-3 border-r border-n-neutral-lighter flex-col">
      <nav className="p-4 flex flex-col flex-1 gap-2">
        {menuItems
          .filter((item) => item.id !== "central-ajuda")
          .map((item) => (
            <Link key={item.label} to={item.to}>
              <Button
                variant="ghost"
                size="sm"
                className={`justify-between w-full rounded-full font-normal hover:bg-white/50 ${
                  isActive(item.to) ? "bg-white text-n-text" : ""
                }`}
              >
                <div className="flex items-center gap-2">
                  {item.icon}
                  {item.label}
                </div>
                {sidebarAlerts[item.id] && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  >
                    <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                  </motion.div>
                )}
              </Button>
            </Link>
          ))}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setFaqOpen(!faqOpen)}
          className={`justify-between w-full rounded-full font-normal transition-all duration-200 ${
            blockActive ? "bg-white text-n-text shadow-sm" : "hover:bg-white/50"
          }`}
        >
          <div className="flex items-center gap-2">
            <MdHelpOutline size={24} className="text-n-text-primary" />
            Central de ajuda
          </div>
          <div className="flex items-center gap-2">
            {sidebarAlerts["central-ajuda"] && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              >
                <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
              </motion.div>
            )}
            <motion.div
              animate={{ rotate: faqOpen ? 180 : 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <ChevronDown className="w-4 h-4" />
            </motion.div>
          </div>
        </Button>

        <AnimatePresence>
          {faqOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0, y: -10 }}
              animate={{ opacity: 1, height: "auto", y: 0 }}
              exit={{ opacity: 0, height: 0, y: -10 }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
                height: { duration: 0.25 },
              }}
              className="overflow-hidden"
            >
              <div className="ml-6 space-y-2">
                {supportLinks.map((link, index) => (
                  <motion.div
                    key={link.to}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: index * 0.1,
                      duration: 0.3,
                      ease: "easeOut",
                    }}
                  >
                    <Link
                      to={link.to}
                      className={`group flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 hover:bg-white/50 ${
                        isActive(link.to)
                          ? "bg-white text-n-green  shadow-sm"
                          : "text-gray-700 hover:text-n-green"
                      }`}
                    >
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 transition-all duration-200 ${
                          isActive(link.to)
                            ? link.color + " shadow-sm"
                            : "bg-gray-300 group-hover:" + link.color
                        }`}
                      />

                      <span
                        className={`text-sm  transition-colors duration-200 ${
                          isActive(link.to)
                            ? "text-n-green"
                            : "text-n-text group-hover:text-n-green"
                        }`}
                      >
                        {link.label}
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <div className="mt-auto">
        <div className="border-t border-gray-300" />

        {/* Seção de configurações comentada */}
        {/* <div className="px-4">
          <Link
            to="/cliente/configuracoes"
            className="flex items-center gap-2 px-3 py-6 text-sm font-medium text-gray-800 hover:bg-gray-200 rounded-lg"
          >
            <MdSettings size={18} className="text-n-text-primary" />
            Configurações
          </Link>
        </div> */}

        <div className="border-t border-gray-300" />

        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 bg-gray-300 rounded-full flex-shrink-0" />
            <div className="flex flex-col min-w-0 flex-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-sm font-medium text-gray-800 truncate cursor-help">
                      {displayName}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>{displayName}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-n-text-neutral truncate cursor-help">
                      {email}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>{email}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* Novo menu ancorado */}
          <UserSessionMenu
            displayName={displayName}
            email={email}
            onSignOut={signOut}
          />
        </div>
      </div>
    </aside>
  );
}
